import { nonEmptyTrimmedStringFromUnknown } from "../protocol/shared.js";
import { clientMessageIdSchema } from "../protocol/client-message-id.js";
import { agentStreamToolPartSchema } from "../protocol/agent-stream.js";
import { openMeldDaemonOriginalErrorSchema } from "../daemon/task-result.js";
import { wakeTraceWaterfallStepNameSchema, wakeTraceWaterfallStepSchema, wakeTraceWaterfallSummaryStatusSchema } from "./space-wake-trace-waterfall.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/space-message-details.ts
const nullableTrimmedStringSchema = z$1.string().trim().min(1).nullable();
const nullableReasonCodeSchema = z$1.string().trim().min(1).max(512).nullable();
const spaceMessageDetailsFactStateSchema = z$1.enum([
	"recorded",
	"not_recorded",
	"redacted",
	"not_applicable"
]);
const spaceMessageDetailsFactSourceSchema = z$1.enum([
	"space_signal",
	"space_signal_profile",
	"reply_signal",
	"wake_trace",
	"wake_trace_execution_context",
	"wake_trace_correlation",
	"none"
]);
const spaceMessageDetailsStringFactSchema = z$1.strictObject({
	state: spaceMessageDetailsFactStateSchema,
	value: nullableTrimmedStringSchema,
	source: spaceMessageDetailsFactSourceSchema
}).superRefine((value, context) => {
	if (value.state === "recorded" && value.value === null) context.addIssue({
		code: "custom",
		message: "recorded facts must include a value",
		path: ["value"]
	});
	if (value.state !== "recorded" && value.value !== null) context.addIssue({
		code: "custom",
		message: "only recorded facts may include a value",
		path: ["value"]
	});
});
const spaceMessageDetailsSectionStateSchema = z$1.enum([
	"recorded",
	"not_recorded",
	"not_applicable"
]);
const spaceMessageDetailsSignalTypeSchema = z$1.enum([
	"signal.text",
	"signal.status",
	"signal.system_notice"
]);
const spaceMessageDetailsActorSchema = z$1.strictObject({
	profileId: z$1.string().trim().min(1),
	profileKind: z$1.enum([
		"human",
		"agent",
		"system"
	]),
	displayName: z$1.string().trim().min(1),
	profileName: nullableTrimmedStringSchema,
	profileHandle: nullableTrimmedStringSchema
});
const spaceMessageDetailsMessageSectionSchema = z$1.strictObject({
	signalId: z$1.string().trim().min(1),
	signalType: spaceMessageDetailsSignalTypeSchema,
	sentAtMs: z$1.int().nonnegative(),
	actor: spaceMessageDetailsActorSchema
});
const spaceMessageDetailsAgentContextSchema = z$1.strictObject({
	state: spaceMessageDetailsSectionStateSchema,
	profileId: spaceMessageDetailsStringFactSchema,
	profileName: spaceMessageDetailsStringFactSchema,
	agentControllerRef: spaceMessageDetailsStringFactSchema,
	agentControllerConversationId: spaceMessageDetailsStringFactSchema,
	workspacePath: spaceMessageDetailsStringFactSchema,
	openMeldServiceRuntimeId: spaceMessageDetailsStringFactSchema,
	bindingId: spaceMessageDetailsStringFactSchema,
	workspaceFingerprint: spaceMessageDetailsStringFactSchema
});
const spaceMessageDetailsWakeTargetSchema = z$1.strictObject({
	targetProfileId: z$1.string().trim().min(1),
	targetLabel: nullableTrimmedStringSchema,
	status: wakeTraceWaterfallSummaryStatusSchema.optional(),
	terminalStep: wakeTraceWaterfallStepNameSchema.nullable().optional(),
	reasonCode: nullableReasonCodeSchema.optional(),
	originalError: openMeldDaemonOriginalErrorSchema.optional(),
	agentContext: spaceMessageDetailsAgentContextSchema.optional(),
	steps: z$1.array(wakeTraceWaterfallStepSchema).optional()
});
const spaceMessageDetailsWakeContextSchema = z$1.strictObject({
	state: spaceMessageDetailsSectionStateSchema,
	sourceSignalId: spaceMessageDetailsStringFactSchema,
	clientMessageId: spaceMessageDetailsStringFactSchema,
	targets: z$1.array(spaceMessageDetailsWakeTargetSchema)
});
const spaceMessageDetailsReplyContextSchema = z$1.strictObject({
	state: spaceMessageDetailsSectionStateSchema,
	replyingToSignalId: spaceMessageDetailsStringFactSchema,
	replyingToAuthor: spaceMessageDetailsStringFactSchema,
	previewText: spaceMessageDetailsStringFactSchema,
	resolved: z$1.boolean()
});
const spaceMessageDetailsToolActivitySchema = z$1.strictObject({
	toolName: z$1.string().trim().min(1).max(160),
	state: z$1.enum([
		"input-available",
		"output-available",
		"output-error"
	]),
	startedAtMs: z$1.int().nonnegative(),
	updatedAtMs: z$1.int().nonnegative(),
	work: agentStreamToolPartSchema.shape.work
}).superRefine((value, context) => {
	if (value.updatedAtMs < value.startedAtMs) context.addIssue({
		code: "custom",
		message: "updatedAtMs must not precede startedAtMs",
		path: ["updatedAtMs"]
	});
});
const spaceMessageDetailsWorkingDetailsSchema = z$1.strictObject({
	state: spaceMessageDetailsSectionStateSchema,
	tools: z$1.array(spaceMessageDetailsToolActivitySchema).max(50),
	truncated: z$1.boolean()
}).superRefine((value, context) => {
	if (value.state === "recorded" && value.tools.length === 0) context.addIssue({
		code: "custom",
		message: "recorded working details must include tool activity",
		path: ["tools"]
	});
	if (value.state !== "recorded" && value.tools.length > 0) context.addIssue({
		code: "custom",
		message: "only recorded working details may include tool activity",
		path: ["tools"]
	});
	if (value.state !== "recorded" && value.truncated) context.addIssue({
		code: "custom",
		message: "only recorded working details may be truncated",
		path: ["truncated"]
	});
});
const spaceMessageDetailsTraceSelectorSchema = z$1.strictObject({
	kind: z$1.enum([
		"signal",
		"client_message",
		"wake_source_signal",
		"dispatch"
	]),
	label: z$1.string().trim().min(1),
	value: z$1.string().trim().min(1),
	targetProfileId: nullableTrimmedStringSchema
});
const spaceMessageDetailsDiagnosticsSchema = z$1.strictObject({
	signalId: z$1.string().trim().min(1),
	traceSelectors: z$1.array(spaceMessageDetailsTraceSelectorSchema)
});
const getSpaceMessageDetailsInputSchema = z$1.strictObject({
	spaceId: nonEmptyTrimmedStringFromUnknown,
	signalId: nonEmptyTrimmedStringFromUnknown
});
const getSpaceMessageDetailsResponseSchema = z$1.strictObject({
	query: z$1.strictObject({
		spaceId: z$1.string().trim().min(1),
		signalId: z$1.string().trim().min(1)
	}),
	message: spaceMessageDetailsMessageSectionSchema,
	agentContext: spaceMessageDetailsAgentContextSchema,
	wakeContext: spaceMessageDetailsWakeContextSchema,
	replyContext: spaceMessageDetailsReplyContextSchema,
	workingDetails: spaceMessageDetailsWorkingDetailsSchema.optional(),
	diagnostics: spaceMessageDetailsDiagnosticsSchema
});
clientMessageIdSchema.nullable();
//#endregion
export { getSpaceMessageDetailsInputSchema, getSpaceMessageDetailsResponseSchema, spaceMessageDetailsActorSchema, spaceMessageDetailsAgentContextSchema, spaceMessageDetailsDiagnosticsSchema, spaceMessageDetailsFactSourceSchema, spaceMessageDetailsFactStateSchema, spaceMessageDetailsMessageSectionSchema, spaceMessageDetailsReplyContextSchema, spaceMessageDetailsSectionStateSchema, spaceMessageDetailsSignalTypeSchema, spaceMessageDetailsStringFactSchema, spaceMessageDetailsToolActivitySchema, spaceMessageDetailsTraceSelectorSchema, spaceMessageDetailsWakeContextSchema, spaceMessageDetailsWakeTargetSchema, spaceMessageDetailsWorkingDetailsSchema };

//# sourceMappingURL=space-message-details.js.map